// PremiumCreditsPurchaseModal.jsx
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Modal, ButtonToolbar } from 'reactstrap';
import Button from '@material-ui/core/Button';
import { Tooltip } from '@material-ui/core';
import classNames from 'classnames';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { setUserProfile } from '../../../redux/actions/authActions';

import SnackBar from '../SimpleSnackBar';
import StripeCardSetupModal from './../payment/stripe/StripeCardSetupModal';

import CloseIcon from 'mdi-react/CloseIcon';
import HelpIcon from 'mdi-react/HelpCircleOutlineIcon';
import LockIcon from 'mdi-react/LockOutlineIcon';
import StarIcon from 'mdi-react/StarOutlineIcon';
import TrendingUpIcon from 'mdi-react/TrendingUpIcon';
import RocketIcon from 'mdi-react/RocketIcon';
import CheckCircleIcon from 'mdi-react/CheckCircleIcon';
import CrownIcon from 'mdi-react/CrownIcon';

import NextIcon from 'mdi-react/SkipNextIcon';
import PremiumCreditIcon from 'mdi-react/PlusCircleMultipleOutlineIcon';
import {
	_formatNumber,
	_formatNumberDecimal
} from '../../../shared/helpers/utils';
import { _axiosCall } from '../../helpers/apicalls';
import './PremiumCreditsPurchaseModal.scss';
import { Publisher } from '../../pubsub/publisher';

var apiUrl = process.env.API_ROOT;

// Standard single-credit baseline price and NYS tax
var BASE_PRICE_PER_CREDIT = 0.80;
var NYS_TAX_RATE = 0.08875; // 8.875%

// Stripe product ID for premium credits (no price attached — amount is set per transaction)
var STRIPE_PREMIUM_CREDITS_PRODUCT_ID =
	process.env.NODE_ENV === "development" || process.env.NODE_ENV === "local"
		? 'prod_TzWUHN69NLB7WH'
		: 'prod_TzWWtocat4f6kG';

// Stripe tax rate ID for NYS Sales Tax (same as subscription plans in plans.js)
var STRIPE_TAX_RATE_ID =
	process.env.NODE_ENV === "development" || process.env.NODE_ENV === "local"
		? 'txr_1HPuUwG44QC64PsRi91xssbx'
		: 'txr_1HCVNJG44QC64PsRdrtshCba';

// Map of credits -> services unlocked
// Add more services here later as needed.
export var premiumCreditServices = [
	{
		id: 'contact',
		creditsRequired: 1,
		shortLabel: 'Owner contact info',
		description: 'Email and phone for unit owner',
		exampleLabel: 'owner contacts (email + phone)'
	},
	{
		id: 'profile',
		creditsRequired: 5,
		shortLabel: 'Full owner profile',
		description: 'Work, education, etc.',
		exampleLabel: 'owner profiles (work, education, more)'
	},
	{
		id: 'company_basic',
		creditsRequired: 1,
		shortLabel: 'LLC DOS/Tax Search',
		description: 'owners',
		exampleLabel: 'search DOS registration'
	},
	{
		id: 'company_advanced',
		creditsRequired: 5,
		shortLabel: 'True LLC owner',
		description: 'True corp owners',
		exampleLabel: 'true corp owners (LLCs, Trusts, etc)'
	}
];

// Self-serve packs
export var premiumCreditPacks = [
	{
		id: 'pack_1',
		credits: 25,
		label: 'Starter',
		highlight: 'Targeted Outreach',
		icon: StarIcon,
		pricePerCredit: 0.8,
		basePricePerCredit: BASE_PRICE_PER_CREDIT
	},
	{
		id: 'pack_2',
		credits: 100,
		label: 'Growth',
		highlight: 'Most Popular',
		icon: TrendingUpIcon,
		pricePerCredit: 0.75,
		basePricePerCredit: BASE_PRICE_PER_CREDIT
	},
	{
		id: 'pack_3',
		credits: 500,
		label: 'Power',
		highlight: 'Best Value',
		icon: RocketIcon,
		pricePerCredit: 0.65,
		basePricePerCredit: BASE_PRICE_PER_CREDIT
	},
	{
		id: 'pack_4',
		credits: 1000,
		label: 'Elite',
		highlight: 'Maximum Savings',
		icon: CrownIcon,
		pricePerCredit: 0.60, // deeper discount for volume
		basePricePerCredit: BASE_PRICE_PER_CREDIT
	}
];


export function getPackPrice(pack) {
	if (!pack) {
		return 0;
	}
	return pack.credits * pack.pricePerCredit;
}

export function getPackSavings(pack) {
	if (!pack) {
		return null;
	}
	var baseline = pack.credits * pack.basePricePerCredit;
	var actual = getPackPrice(pack);
	var savings = baseline - actual;
	if (savings <= 0) {
		return null;
	}
	var pct = Math.round((savings / baseline) * 100);
	return { savings: savings, percent: pct };
}

// Given credits and a list of services, return how many times
// each service can be unlocked with that pack.
export function getUsageExamples(credits, services) {
	var i;
	var examples = [];
	if (!services || !services.length) {
		return examples;
	}

	for (i = 0; i < services.length; i++) {
		var svc = services[i];
		if (!svc || !svc.creditsRequired || svc.creditsRequired <= 0) {
			continue;
		}
		var maxUses = Math.floor(credits / svc.creditsRequired);
		examples.push({
			service: svc,
			maxUses: maxUses
		});
	}

	return examples;
}

class PremiumCreditsPurchaseModal extends Component {
	constructor(props) {
		super(props);
		this.state = {
			modal: false,
			selectedPackId: 'pack_2',
			step: 'select', // 'select' | 'confirm'
			doPaymentProgress: false,

			// Stripe / payment state
			customer: null,
			paymentMethod: null,
			showPaymentModal: false,
			setupClientSecret: null,

			// Amounts for current purchase
			subtotalAmount: 0,
			taxAmount: 0,
			totalAmount: 0,
			totalAmountWithTax: 0,
			currentPack: null,

			// SnackBar
			snackShow: false,
			snackMessage: '',
			snackType: 'success'
		};

		this.toggle = this.toggle.bind(this);
		this.open = this.open.bind(this);
		this.close = this.close.bind(this);
		this.handleSelectPack = this.handleSelectPack.bind(this);
		this.goToConfirmStep = this.goToConfirmStep.bind(this);
		this.handleConfirm = this.handleConfirm.bind(this);

		this._maybeSetupStripeCustomerAndCard = this._maybeSetupStripeCustomerAndCard.bind(this);
		this._createSetupIntent = this._createSetupIntent.bind(this);
		this._checkIfCustomerHasCard = this._checkIfCustomerHasCard.bind(this);
		this._doPayment = this._doPayment.bind(this);
		this._applyPremiumCredits = this._applyPremiumCredits.bind(this);
		this.snackOpen = this.snackOpen.bind(this);
		this.snackClose = this.snackClose.bind(this);
	}

	resetState() {
		this.setState({
			modal: false,
			selectedPackId: 'pack_1',
			step: 'select',
			doPaymentProgress: false,
			customer: null,
			paymentMethod: null,
			showPaymentModal: false,
			setupClientSecret: null,
			subtotalAmount: 0,
			taxAmount: 0,
			totalAmount: 0,
			totalAmountWithTax: 0,
			currentPack: null
		});
	}

	snackOpen(message, type) {
		var snackType = type || 'success';
		this.setState({
			snackShow: true,
			snackMessage: message,
			snackType: snackType
		});
	}

	snackClose(event, reason) {
		if (reason === 'clickaway') {
			return;
		}
		this.setState({
			snackShow: false,
			snackMessage: ''
		});
	}

	toggle(event) {
		if (event && typeof event.stopPropagation === 'function') {
			event.stopPropagation();
		}
		var nextOpen = !this.state.modal;

		if (!nextOpen) {
			// Closing the modal resets the flow
			this.resetState();
		} else {
			this.setState({ modal: nextOpen });
			// Preload Stripe customer / payment method if possible
			this._initStripeState();

			// Track premium credit modal open event
			Publisher.publish('track-mp.premiumCreditModalOpen', {
				user: this.props.user,
				customData: {
					source: this.props.source || 'unknown'
				}
			});
		}

		if (this.props.onToggle && typeof this.props.onToggle === 'function') {
			this.props.onToggle(nextOpen);
		}
	}

	open() {
		this.setState({ modal: true });

		// Track premium credit modal open event
		Publisher.publish('track-mp.premiumCreditModalOpen', {
			user: this.props.user,
			customData: {
				source: this.props.source || 'unknown'
			}
		});
	}

	close() {
		this.setState({ modal: false });
	}

	_initStripeState() {
		var user = this.props.user;
		var _this = this;

		if (!user) {
			return;
		}

		if (user.customerId) {
			this.setState({ customer: { id: user.customerId } });

			_axiosCall({
				url: apiUrl + '/buyerslist/stripe/get-payment-methods',
				method: 'post',
				query: {
					customer_id: user.customerId
				}
			}).then(function (res) {
				if (res && res.success && res.methods && res.methods.length > 0) {
					_this.setState({ paymentMethod: res.methods[0] });
				} else if (user.subscriptionId) {
					// Fallback: try to get payment method and customer from the subscription
					_axiosCall({
						url: apiUrl + '/buyerslist/stripe/get-subscription',
						method: 'post',
						query: {
							subscription_id: user.subscriptionId
						}
					}).then(function (subRes) {
						if (subRes && subRes.paymentMethod && subRes.paymentMethod.id) {
							var updates = { paymentMethod: subRes.paymentMethod };
							// Use the subscription's customer so the PM is attached to the right customer
							if (subRes.customer && subRes.customer.id) {
								updates.customer = { id: subRes.customer.id };
							}
							_this.setState(updates);
						}
					}).catch(function (subErr) {
						console.error('Error fetching subscription payment method for premium credits', subErr);
					});
				}
			}).catch(function (err) {
				// Silent failure, user can still proceed and set up card later
				console.error('Error preloading payment methods for premium credits', err);
			});
		}
	}

	handleSelectPack(packId) {
		this.setState({ selectedPackId: packId });
	}

	goToConfirmStep() {
		if (!this.state.selectedPackId) {
			return;
		}
		this.setState({ step: 'confirm' });
	}

	handleConfirm() {
		var selectedPack = null;
		var i;
		for (i = 0; i < premiumCreditPacks.length; i++) {
			if (premiumCreditPacks[i].id === this.state.selectedPackId) {
				selectedPack = premiumCreditPacks[i];
				break;
			}
		}

		if (!selectedPack) {
			return;
		}

		var subtotalAmount = getPackPrice(selectedPack);
		var taxAmount = subtotalAmount * NYS_TAX_RATE;
		var totalAmount = subtotalAmount + taxAmount;

		var _this = this;

		this.setState(
			{
				doPaymentProgress: true,
				currentPack: selectedPack,
				subtotalAmount: subtotalAmount,
				taxAmount: taxAmount,
				totalAmount: subtotalAmount,
				totalAmountWithTax: totalAmount
			},
			function () {
				if (!_this.state.paymentMethod || !_this.state.paymentMethod.id) {
					_this._maybeSetupStripeCustomerAndCard();
				} else {
					_this._doPayment();
				}
			}
		);
	}

	_maybeSetupStripeCustomerAndCard() {
		var user = this.props.user;
		var customer = this.state.customer;
		var paymentMethod = this.state.paymentMethod;
		var _this = this;

		if (!user) {
			this.snackOpen('You need to be logged in to buy Premium Credits.', 'error');
			this.setState({ doPaymentProgress: false });
			return;
		}

		if (paymentMethod && paymentMethod.id) {
			this._doPayment();
			return;
		}

		var customerId = null;
		if (customer && customer.id) {
			customerId = customer.id;
		} else if (user && user.customerId) {
			customerId = user.customerId;
		}

		if (!customerId) {
			// Create basic customer
			_axiosCall({
				url: apiUrl + '/buyerslist/stripe/create-basic-customer',
				method: 'post',
				query: {
					userId: user.id,
					name: (user.firstName && user.lastName) ? user.firstName + ' ' + user.lastName : '',
					email: user.email
				}
			}).then(function (res) {
				if (res && res.customerId) {
					_this.props.setUserProfile(Object.assign({}, user, { customerId: res.customerId }));
					_this.setState({ customer: { id: res.customerId } }, function () {
						_this._checkIfCustomerHasCard(res.customerId);
					});
				} else {
					_this.snackOpen('Failed to set up payment. Please try again.', 'error');
					_this.setState({ doPaymentProgress: false });
				}
			}).catch(function (err) {
				console.error('Error creating Stripe customer for premium credits', err);
				_this.snackOpen('Failed to set up payment. Please try again.', 'error');
				_this.setState({ doPaymentProgress: false });
			});
		} else {
			this.setState({ customer: { id: customerId } }, function () {
				_this._checkIfCustomerHasCard(customerId);
			});
		}
	}

	_createSetupIntent() {
		var customer = this.state.customer;
		var _this = this;

		if (!customer || !customer.id) {
			this.snackOpen('Missing Stripe customer. Please try again.', 'error');
			this.setState({ doPaymentProgress: false });
			return;
		}

		_axiosCall({
			url: apiUrl + '/buyerslist/stripe/create-setup-intent',
			method: 'post',
			query: {
				customer_id: customer.id
			}
		}).then(function (res) {
			if (res && res.client_secret) {
				_this.setState({
					setupClientSecret: res.client_secret,
					showPaymentModal: true
				});
			} else {
				_this.snackOpen('Failed to initiate payment setup. Please try again.', 'error');
				_this.setState({ doPaymentProgress: false });
			}
		}).catch(function (err) {
			console.error('Failed to create SetupIntent for premium credits', err);
			_this.snackOpen('Failed to initiate payment setup. Please try again.', 'error');
			_this.setState({ doPaymentProgress: false });
		});
	}

	_checkIfCustomerHasCard(customerId) {
		var _this = this;

		_axiosCall({
			url: apiUrl + '/buyerslist/stripe/get-payment-methods',
			method: 'post',
			query: {
				customer_id: customerId
			}
		}).then(function (res) {
			if (res && res.success && res.methods && res.methods.length > 0) {
				_this.setState({ paymentMethod: res.methods[0] }, function () {
					_this._doPayment();
				});
			} else {
				_this._createSetupIntent();
			}
		}).catch(function (err) {
			console.error('Error checking payment methods for premium credits', err);
			_this.snackOpen('Error checking payment methods. Please try again.', 'error');
			_this._createSetupIntent();
		});
	}

	_doPayment() {
		if (this.state.doPaymentProgress !== true) {
			this.setState({ doPaymentProgress: true });
		}

		var customer = this.state.customer;
		var paymentMethod = this.state.paymentMethod;
		var totalAmountWithTax = this.state.totalAmountWithTax;
		var currentPack = this.state.currentPack;
		var _this = this;
		var user = this.props.user;

		if (!customer || !customer.id || !paymentMethod || !paymentMethod.id || !currentPack) {
			this.snackOpen('Missing payment details. Please try again.', 'error');
			this.setState({ doPaymentProgress: false });
			return;
		}

		if (!user || !user.id) {
			this.snackOpen('Missing user information. Please refresh and try again.', 'error');
			this.setState({ doPaymentProgress: false });
			return;
		}

		var stripeAmount = parseInt(parseFloat(totalAmountWithTax) * 100, 10);
		var description =
			'Purchase of ' +
			_formatNumber(currentPack.credits) +
			' Marketproof Premium Credits (' +
			currentPack.label +
			')';

		var creditConfig = {
			id: currentPack.id,
			label: currentPack.label,
			credits: currentPack.credits,
			pricePerCredit: currentPack.pricePerCredit,
			basePricePerCredit: currentPack.basePricePerCredit,
			subtotal: this.state.subtotalAmount,
			tax: this.state.taxAmount,
			total: this.state.totalAmountWithTax,
			stripeProductId: STRIPE_PREMIUM_CREDITS_PRODUCT_ID,
			taxCode: STRIPE_TAX_RATE_ID
		};

		_axiosCall({
			url: apiUrl + '/buyerslist/stripe/onetime',
			method: 'post',
			query: {
				customer: customer.id,
				amount: stripeAmount,
				payment_method: paymentMethod.id,
				description: description,

				// Explicitly identify this as a premium credits purchase
				payment_type: 'premium_credits',

				// Pass full user object so backend can derive id/email as needed
				user_id: user.id,
				user_email: user.email,
				user: user,

				// Credits info
				credits: currentPack.credits,
				creditConfig: creditConfig
			}
		})
			.then(function (res) {
				var payment = res && res.payment ? res.payment : res;

				if (payment && (payment.status === 'succeeded' || payment.amount_received === stripeAmount)) {
					_this._applyPremiumCredits(res, currentPack);
				} else {
					_this.snackOpen('Your payment was not successful. Please try again.', 'error');
					_this.setState({ doPaymentProgress: false });
				}
			})
			.catch(function (err) {
				console.error('Premium credits onetime payment error', err);
				_this.snackOpen('Something went wrong with your payment. Please try again.', 'error');
				_this.setState({ doPaymentProgress: false });
			});
	}



	// Field name assumption: "premiumCredits" on the user profile.
	// Change this name if your profile uses a different field.



	_applyPremiumCredits(response, selectedPack) {
		var _this = this;

		this.setState({ doPaymentProgress: false });

		if (!selectedPack || !selectedPack.credits) {
			this.snackOpen('Could not apply Premium Credits. Please contact support.', 'error');
			return;
		}

		// If backend returned an updated user profile, use it to refresh local state
		if (response && response.user && this.props.setUserProfile) {
			this.props.setUserProfile(response.user);
		} else {
			console.log('Premium credits response has no user; skipping local profile update', response);
		}

		this.snackOpen('Premium Credits added to your account.');

		if (this.props.onConfirm && typeof this.props.onConfirm === 'function') {
			this.props.onConfirm(selectedPack, function () { });
		}

		this.resetState();
	}



	render() {
		var _this = this;
		var modalClass = classNames({
			'modal-dialog--colored': false,
			'modal-dialog--header': true,
			'modal-dialog--wide': true
		});

		var selectedPackId = this.state.selectedPackId;
		var step = this.state.step;

		var selectedPack = null;
		var i;
		for (i = 0; i < premiumCreditPacks.length; i++) {
			if (premiumCreditPacks[i].id === selectedPackId) {
				selectedPack = premiumCreditPacks[i];
				break;
			}
		}

		var selectedPackPrice = selectedPack ? getPackPrice(selectedPack) : 0;
		var selectedSavings = selectedPack ? getPackSavings(selectedPack) : null;

		var subtotalAmount = selectedPack ? selectedPackPrice : 0;
		var taxAmount = selectedPack ? subtotalAmount * NYS_TAX_RATE : 0;
		var totalAmount = selectedPack ? subtotalAmount + taxAmount : 0;

		var subtotalDisplay = selectedPack
			? _formatNumberDecimal(subtotalAmount)
			: _formatNumberDecimal(0);
		var taxDisplay = selectedPack
			? _formatNumberDecimal(taxAmount)
			: _formatNumberDecimal(0);
		var totalDisplay = selectedPack
			? _formatNumberDecimal(totalAmount)
			: _formatNumberDecimal(0);

		var isSelectStep = step === 'select';
		var isConfirmStep = step === 'confirm';

		// For the top pills, use the first two services (if present)
		var primaryService = premiumCreditServices[0];
		var secondaryService = premiumCreditServices[1];

		var user = this.props.user;

		return (
			<Fragment>
				{/* Stripe card setup modal */}
				{this.state.showPaymentModal && (
					<StripeCardSetupModal
						isOpen={this.state.showPaymentModal}
						clientSecret={this.state.setupClientSecret}
						user={user}
						onClose={function () {
							_this.setState({ showPaymentModal: false, doPaymentProgress: false });
						}}
						onComplete={function () {
							_this.setState({ showPaymentModal: false }, function () {
								var customer = _this.state.customer;
								if (!customer || !customer.id) {
									return;
								}
								_axiosCall({
									url: apiUrl + '/buyerslist/stripe/get-payment-methods',
									method: 'post',
									query: {
										customer_id: customer.id
									}
								}).then(function (res) {
									if (res && res.success && res.methods && res.methods.length > 0) {
										_this.setState({ paymentMethod: res.methods[0] }, function () {
											_this._doPayment();
										});
									} else {
										console.error('No payment method found after setup for premium credits');
										_this.snackOpen('Failed to retrieve payment method after setup.', 'error');
										_this.setState({ doPaymentProgress: false });
									}
								}).catch(function (err) {
									console.error('Failed to retrieve payment method after setup for premium credits', err);
									_this.snackOpen('Failed to retrieve payment method after setup.', 'error');
									_this.setState({ doPaymentProgress: false });
								});
							});
						}}
					/>
				)}

				<SnackBar
					snackShow={this.state.snackShow}
					snackMessage={this.state.snackMessage}
					snackClose={this.snackClose}
					snackDuration={5000}
					snackType={this.state.snackType}
				/>

				{this.props.renderButton &&
					this.props.renderButton({ onClick: this.toggle })}

				<Modal
					isOpen={this.state.modal}
					toggle={this.toggle}
					modalClassName={'ltr-support'}
					className={
						'contact__modal modal-dialog--primary premium-credits-modal ' +
						modalClass
					}
				>
					<div className="modal__header">
						<button
							className="lnr lnr-cross modal__close-btn"
							type="button"
							onClick={this.toggle}
						/>
						<h4 className="text-modal modal__title">
							Buy Premium Credits
						</h4>
					</div>

					<div className="modal__body background_white premium-credits-body">
						<div className="premium-credit-overview">
							<h5 className="premium-credit-overview__title">
								What Are Premium Credits?
							</h5>

							<p className="premium-credit-overview__text">
								Premium Credits <span className="semi-bold-text">unlock higher-value content</span> like contact emails, phone numbers, owner profiles and true owners behind LLCs. <br />They <span className="semi-bold-text">never expire</span> and are purchased separately from your Marketproof Pro subscription.
							</p>

							<a
								href="https://help.marketproof.com/en/articles/13454433-understanding-credits-in-marketproof-pro"
								className="premium-credit-overview__link link-text"
								target="_blank"
								rel="noopener noreferrer"
							>
								Learn more about Premium Credits <HelpIcon size={12} style={{ marginTop: -3 }} />
							</a>
						</div>

						{isSelectStep && (
							<Fragment>
								<hr />
								<div className="premium-credit-header-row">
									<h4 className="premium-credit-header-row__title semi-bold-text">
										Choose a Premium Credit Pack
									</h4>
								</div>

								<div className="premium-credit-packs">
									{premiumCreditPacks.map(function (pack) {
										var isSelected = pack.id === selectedPackId;
										var packPrice = getPackPrice(pack);
										var savings = getPackSavings(pack);
										var usageList = getUsageExamples(
											pack.credits,
											premiumCreditServices
										);
										var IconComponent = pack.icon;

										return (
											<div
												key={pack.id}
												className={
													'premium-pack-card' +
													(isSelected ? ' premium-pack-card--selected' : '')
												}
												onClick={function () {
													_this.handleSelectPack(pack.id);
												}}
											>
												{isSelected && (
													<div className="premium-pack-card__selected-badge">
														<CheckCircleIcon size={36} />
													</div>
												)}
												<div className="premium-pack-card__header">
													<div className="premium-pack-card__icon-wrapper">
														<IconComponent
															size={24}
															className="premium-pack-card__icon"
														/>
													</div>

													<div className="premium-pack-card__label-block">
														<div className="premium-pack-card__label">
															{pack.label}
														</div>
														{pack.highlight && (
															<div className="premium-pack-card__highlight">
																{pack.highlight}
															</div>
														)}
													</div>
												</div>

												<div className="premium-pack-card__content">
													<div>
														<div className="premium-pack-card__credits">
															{_formatNumber(pack.credits)} Premium Credits
														</div>

														<div className="premium-pack-card__price">
															<span className="premium-pack-card__price-main">
																${_formatNumberDecimal(packPrice)}
															</span>
															<span className="premium-pack-card__price-sub">
																one-time payment
															</span>
														</div>

														<div className="premium-pack-card__meta">
															<span className="premium-pack-card__per-credit">
																${pack.pricePerCredit.toFixed(2)} per credit
															</span>

															{savings && (
																<span className="premium-pack-card__savings">
																	Save ${_formatNumber(savings.savings)} (
																	{savings.percent}% off)
																</span>
															)}
														</div>
													</div>

													<div className="premium-pack-card__examples-wrapper">
														<hr className="premium-pack-card__divider" />
														<div className="premium-pack-card__examples-title">
															How you could use this pack
														</div>
														<ul className="premium-pack-card__examples-list">
															{usageList.map(function (usage, index) {
																var svc = usage.service;
																var maxUses = usage.maxUses;
																if (!svc) {
																	return null;
																}

																return (
																	<li key={svc.id}>
																		{index > 0 && (
																			<span className="premium-pack-card__or-prefix">Or </span>
																		)}
																		<span className="semi-bold-text">
																			{_formatNumber(maxUses)}
																		</span>{' '}
																		{svc.exampleLabel}
																	</li>
																);
															})}
														</ul>
													</div>
												</div>
											</div>
										);
									})}
								</div>

								<div className="premium-credit-bulk">
									<h5 className="premium-credit-bulk__title">
										Want contacts for an entire building or other bulk use, contact us.
									</h5>
									<p className="premium-credit-bulk__text">
										For higher-volume outreach or enterprise use, we can set up a
										bulk Premium Credits package with custom pricing.
									</p>
									<a
										href="#"
										className="premium-credit-bulk__link intercom_launch link-text"
										onClick={function (e) {
											e.preventDefault();
											if (window.Intercom) {
												window.Intercom('show');
											}
										}}
									>
										Talk to us about bulk credits →
									</a>
								</div>
							</Fragment>
						)}

						{isConfirmStep && selectedPack && (
							<div className="premium-credit-confirm">
								<h4 className="premium-credit-confirm__title">
									Confirm your purchase
								</h4>
								<p className="premium-credit-confirm__text">
									You are purchasing{' '}
									<strong>
										{_formatNumber(selectedPack.credits)} Premium Credits
									</strong>{' '}
									at ${selectedPack.pricePerCredit.toFixed(2)} per credit.
								</p>

								{selectedSavings && (
									<div className="premium-credit-confirm__savings">
										Compared to single credits, you save{' '}
										<strong>
											${_formatNumber(selectedSavings.savings)} (
											{selectedSavings.percent}%)
										</strong>
										.
									</div>
								)}

								<div className="premium-credit-summary">
									<div className="premium-credit-summary__row">
										<span>Subtotal</span>
										<span>${subtotalDisplay}</span>
									</div>
									<div className="premium-credit-summary__row">
										<span>NYS Sales Tax (8.875%)</span>
										<span>${taxDisplay}</span>
									</div>
									<div className="premium-credit-summary__total">
										<span>Total Due</span>
										<span>${totalDisplay}</span>
									</div>

									<div className="premium-credit-summary__note">
										Premium Credits never expire and can be used across all
										premium features as they are introduced.
									</div>
									<div className="premium-credit-summary__terms">
										By purchasing, you agree to the{' '}
										<a
											href="https://marketproof.com/terms-of-use#data-credits"
											target="_blank"
											rel="noopener noreferrer"
											className="link-text"
										>
											Terms of Use
										</a>.
									</div>
								</div>
							</div>
						)}

						<hr />

						<ButtonToolbar className="modal__footer premium-credit-footer">
							{isSelectStep && (
								<Fragment>
									<Tooltip title="Cancel">
										<Button
											onClick={this.toggle}
											variant="outlined"
											color=""
										>
											Cancel
										</Button>
									</Tooltip>{' '}
									<Tooltip title="Review total and taxes">
										<span>
											<Button
												variant="contained"
												color="primary"
												className="buyersList__primary-button"
												startIcon={<NextIcon />}
												onClick={this.goToConfirmStep}
												disabled={!selectedPack}
											>
												Continue
											</Button>
										</span>
									</Tooltip>
								</Fragment>
							)}

							{isConfirmStep && (
								<Fragment>
									<Tooltip title="Back to plan selection">
										<Button
											onClick={function () {
												_this.setState({ step: 'select' });
											}}
											variant="outlined"
											color=""
										>
											Back
										</Button>
									</Tooltip>{' '}
									<Tooltip title="Confirm and pay">
										<span>
											<Button
												variant="contained"
												color="primary"
												className="buyersList__primary-button"
												startIcon={<LockIcon />}
												onClick={this.handleConfirm}
												disabled={!selectedPack || this.state.doPaymentProgress}
											>
												Confirm &amp; Pay
											</Button>
										</span>
									</Tooltip>
								</Fragment>
							)}
						</ButtonToolbar>

						{this.state.doPaymentProgress && (
							<div className="modal-overlay premium-credit-overlay">
								<h4>Processing Payment</h4>
								<LockIcon size={48} color={'#9600BF'} />
							</div>
						)}
					</div>
				</Modal>
			</Fragment>
		);
	}
}

PremiumCreditsPurchaseModal.propTypes = {
	renderButton: PropTypes.func.isRequired,
	onConfirm: PropTypes.func, // (selectedPack, done) => void
	onToggle: PropTypes.func,
	user: PropTypes.object
};

function mapStateToProps(state) {
	return {
		user: state.user
	};
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators(
		{
			setUserProfile: setUserProfile
		},
		dispatch
	);
}

export default connect(
	mapStateToProps,
	mapDispatchToProps,
	null,
	{ forwardRef: true }
)(PremiumCreditsPurchaseModal);

